import { CommonLoggedInDataContext } from 'CommonLoggedInData';
import React from 'react';
import ContentManagementInterface, {
  ImageCarouselType,
  LeftRightListType,
  StandardHeaderTextType,
  TableType,
} from '../../models/ContentManagementInterface';
import ImageCarousel from '../template/ImageCarousel';
import LeftRightList from '../template/LeftRightList';
import StandardHeaderText from '../template/StandardHeaderText';
import Subtitle from '../template/Subtitle';
import Table from '../template/Table';
import TextBlock from '../template/TextBlock';
import HeroImage from '../template/HeroImage';
import { FundOpportunityEOIInstance } from './controller';

export interface FundOpportunityEOIContentSectionsProps {
  instance: FundOpportunityEOIInstance;
  contentManagementInterface: ContentManagementInterface;
}

/**
 * Renders the content-management sections (StandardHeaderText, ImageCarousel,
 * LeftRightList, Table) for a fund opportunity, honouring the section filter.
 * Read-only: content is edited in NewContentManagementEditor.
 */
export function FundOpportunityEOIContentSections({
  instance,
  contentManagementInterface,
}: FundOpportunityEOIContentSectionsProps): JSX.Element | null {
  if (instance.state.filter === 'Vera' || !contentManagementInterface) {
    return null;
  }

  const backendService = instance.getBackendService();
  const getVideoPlaybackUrl = backendService.getVideoPlaybackUrl.bind(backendService);
  const visible = (id: string) => instance.state.filter === 'All' || instance.state.filter === id;
  // The first section shown keeps its top padding; later sections sit flush under it.
  const isFirst = (index: number, id: string) => index === 0 || instance.state.filter === id;

  return (
    <>
      {contentManagementInterface.content.map((item, index) => {
        if (!visible(item.id)) return null;
        switch (item.type) {
          case 'StandardHeaderText': {
            const block = item as StandardHeaderTextType;
            return (
              <StandardHeaderText
                key={`StandardHeaderText-${block.id}`}
                tone={instance.props.tone}
                heading={block.properties.heading}
                content={block.properties.content}
                id={block.properties.id}
                footnotes={block.properties.footnotes || []}
                firstElement={isFirst(index, block.id)}
              />
            );
          }
          case 'Subtitle':
            return (
              <Subtitle
                key={`Subtitle-${item.id}`}
                id={item.id}
                tone={instance.props.tone}
                heading={item.properties.heading}
                firstElement={isFirst(index, item.id)}
              />
            );
          case 'Text':
            return (
              <TextBlock
                key={`Text-${item.id}`}
                id={item.id}
                tone={instance.props.tone}
                content={item.properties.content}
                firstElement={isFirst(index, item.id)}
              />
            );
          case 'HeroImage':
            return (
              <HeroImage
                key={`HeroImage-${item.id}`}
                id={item.id}
                tone={instance.props.tone}
                imageUrl={item.properties.imageUrl}
                altText={item.properties.altText}
                firstElement={isFirst(index, item.id)}
                printMode={instance.state.printMode}
              />
            );
          case 'ImageCarousel': {
            const block = item as ImageCarouselType;
            return (
              <CommonLoggedInDataContext.Provider
                key={`ImageCarousel-${block.id}`}
                value={instance.props.userContext}
              >
                <ImageCarousel
                  {...block.properties}
                  id={block.id}
                  modelType="FundOpportunity"
                  modelId={instance.state.fundOpportunity!.id}
                  printMode={instance.state.printMode}
                  footnotes={block.properties.footnotes || []}
                  advisorMode={instance.props.advisorMode}
                  getVideoPlaybackUrl={getVideoPlaybackUrl}
                />
              </CommonLoggedInDataContext.Provider>
            );
          }
          case 'LeftRightList': {
            const block = item as LeftRightListType;
            return (
              <LeftRightList
                key={`LeftRightList-${block.id}`}
                {...block.properties}
                id={block.id}
                footnotes={block.properties.footnotes || []}
                firstElement={isFirst(index, block.id)}
              />
            );
          }
          case 'Table': {
            const block = item as TableType;
            return (
              <Table
                key={`Table-${block.id}`}
                {...block.properties}
                id={block.id}
                footnotes={block.properties.footnotes || []}
                firstElement={isFirst(index, block.id)}
              />
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
}
